import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Modal, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';

import DropZone from 'components/shared/Dropzone';
import EntityType, { ENTITY_TYPE_OPTIONS } from 'lib/enums/EntityType';
import {
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  PRODUCT_ENRICHMENT_RESOURCES_UPLOAD_PDF,
} from 'lib/networking/endpoints';
import { ProductEnrichmentResourcesFileType } from 'lib/enums/ProductEnrichmentResourcesFileType';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import SwalDialog from 'lib/utils/toast';
import usePost from 'lib/hooks/usePost';
import useGet from 'lib/hooks/useGet';

const PRODUCT_ENRICHMENT_RESOURCES_PDF_FOLDER = 'product_enrichment_resources/pdf';

function AddPDFFileModal({ show, onHide, refetch }) {
  const [entityType, setEntityType] = useState({});
  const [manufacturer, setManufacturer] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [file, setFile] = useState(null);

  const { data: { data: globalVendors } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN);

  const entityNames = useMemo(() => {
    if (entityType.value === EntityType.DISTRIBUTOR && globalVendors) {
      return globalVendors.map(vendor => ({
        value: vendor.id,
        label: vendor.name,
      }));
    }
    return [];
  }, [entityType, globalVendors]);

  const { postData: uploadPDFFile, loading: isSubmitting } = usePost(
    PRODUCT_ENRICHMENT_RESOURCES_UPLOAD_PDF,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Specification Sheet uploaded successfully',
      }).then(() => onHide());
      refetch();
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred while uploading: ${error?.response?.data?.message}`,
      });
    },
  );

  const onSubmit = () => {
    if (!file) {
      SwalDialog('error', 'Please upload a specification sheet', 'Error', 'center');
      return;
    }

    if (!entityType.value) {
      SwalDialog('error', 'Please select an entity type', 'Error', 'center');
      return;
    }

    if (entityType.value === EntityType.MANUFACTURER && !manufacturer) {
      SwalDialog('error', 'Please select a manufacturer', 'Error', 'center');
      return;
    }

    if (entityType.value === EntityType.DISTRIBUTOR && !vendor) {
      SwalDialog('error', 'Please select a distributor', 'Error', 'center');
      return;
    }

    const data = {
      manufacturer_id: entityType.value === EntityType.MANUFACTURER ? manufacturer?.value : null,
      vendor_id: entityType.value === EntityType.DISTRIBUTOR ? vendor?.value : null,
      file_type: ProductEnrichmentResourcesFileType.PDF_FILE,
      upload_location_url: file?.s3_path,
      source: 'spec_sheet_url',
    };
    uploadPDFFile(data);
  };

  const handleReset = () => {
    setEntityType({});
    setManufacturer(null);
    setVendor(null);
    setFile(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleReset} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Upload Specification Sheet</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Entity Type</Form.Label>
                <Select
                  value={entityType}
                  options={ENTITY_TYPE_OPTIONS}
                  onChange={e => {
                    setEntityType(e || {});
                    setManufacturer(null);
                    setVendor(null);
                  }}
                  isSearchable
                  isClearable
                  placeholder="Select Entity Type"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  {entityType.value === EntityType.MANUFACTURER ? 'Manufacturer' : 'Distributor'}
                </Form.Label>
                {entityType.value === EntityType.MANUFACTURER ? (
                  <SearchableScrollableSelect
                    onChange={setManufacturer}
                    fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                    optionMapper={manufacturer => ({
                      value: manufacturer.id,
                      label: manufacturer.name,
                    })}
                    autoFocus={false}
                    placeholder="Select Manufacturer"
                    value={manufacturer}
                  />
                ) : (
                  <Select
                    value={vendor}
                    options={entityNames}
                    onChange={e => setVendor(e)}
                    isSearchable
                    isClearable
                    placeholder="Select Distributor"
                  />
                )}
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Upload Specification Sheet (PDF)</Form.Label>
            <DropZone
              text="Drag and drop your specification sheet (PDF) here, or click to select the file"
              onUploadSuccess={newFiles => setFile(newFiles?.[0])}
              filePath={PRODUCT_ENRICHMENT_RESOURCES_PDF_FOLDER}
              fileUrls={file ? [file.presigned_url] : []}
              acceptedFileTypes=".pdf"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleReset} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={
            isSubmitting ||
            !file ||
            !entityType.value ||
            (entityType.value === EntityType.MANUFACTURER && !manufacturer) ||
            (entityType.value === EntityType.DISTRIBUTOR && !vendor)
          }
        >
          {isSubmitting ? 'Uploading...' : 'Upload Specification Sheet'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

AddPDFFileModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};

export default AddPDFFileModal;
